import { AccountClaimBanner } from "@/site/components/auth/account-claim";
import { authSurfaceFromNext, trackFunnel } from "@/lib/funnel-events";
import { AuthSocial } from "@/site/components/auth/auth-social";
import { oauthErrorMessage } from "@/site/components/auth/auth-utils";
import { DemoPersonaPicker } from "@/site/components/auth/demo-persona-picker";
import { ForgotPasswordForm } from "@/site/components/auth/forgot-password-form";
import { LoginForm } from "@/site/components/auth/login-form";
import { RegisterForm } from "@/site/components/auth/register-form";
import { SimpleFooter, SiteFooter } from "@/site/components/site-footer";
import { SiteHeader } from "@/site/components/site-header";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export type AuthStage = "login" | "register" | "forgot";

/** Legacy `getMembershipGuardProps()` defaults for `location="auth-page"`. */
const COPY: Record<AuthStage, { title: string; subtitle: string }> = {
  login: { title: "Welcome!", subtitle: "Sign in to continue" },
  register: { title: "Become a member", subtitle: "Create your account" },
  forgot: {
    title: "Forgot Password?",
    subtitle: "Enter your email to reset your password",
  },
};

const STAGE_PATH: Record<AuthStage, "/login" | "/register" | "/forgot"> = {
  login: "/login",
  register: "/register",
  forgot: "/forgot",
};

/**
 * The auth wall — the page behind `/login`, `/register` and `/forgot`.
 *
 * This is a port of the legacy `pages/auth-page` → `components/auth/authwall`
 * pair: a serif title block, the three social buttons, the "OR use an email
 * address" rule, then the stage's form. `stage` is driven by the route rather
 * than by local state, so switching between sign-in and register changes the
 * URL (and keeps the query string, including `?next=`) exactly as it used to.
 *
 * Nothing here navigates on success. `__root.tsx` owns the post-auth redirect
 * for every path into the app — password login, OAuth return, a soft-login
 * link, or someone who was already signed in when they hit the page.
 */
export default function AuthPage({ stage = "login" }: { stage?: AuthStage }) {
  const navigate = useNavigate();
  // What the member typed on the login form before pressing "Forgot password?" (BDAY-474).
  const [forgotEmail, setForgotEmail] = useState("");

  // OAuth callbacks bounce failures back here as `?error=<code>`.
  const oauthError = oauthErrorMessage(
    new URLSearchParams(window.location.search).get("error") ?? undefined,
  );

  const goToStage = (stageNext: AuthStage) => {
    // Carry `?next=` across the switch (legacy `onAuthSwitch` preserved the query
    // string); the spent `?error=` code is dropped.
    const next = new URLSearchParams(window.location.search).get("next");
    void navigate({ to: STAGE_PATH[stageNext], search: next ? { next } : {} });
  };

  const isDevEnv = (import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE) === "development";

  // PRD 13 K1: legacy's per-surface auth-wall beacons (/mycards/login, /card/read/register,
  // /dob…/register): the full-page wall, keyed by the page `?next=` came from.
  useEffect(() => {
    const next = new URLSearchParams(window.location.search).get("next");
    trackFunnel("authwall_shown", { surface: authSurfaceFromNext(next), stage });
  }, [stage]);

  const { title, subtitle } = COPY[stage];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* SiteHeader routes /login, /register and /forgot to its `simpleChrome`
          branch — the tan bar with the logo and "Welcome Guest", same as legacy. */}
      <SiteHeader />

      <main id="main-content" className="min-h-[80vh]">
        <div className="mx-auto max-w-[600px] px-5 py-10">
          <div className="flex w-full flex-col items-center gap-4">
            <div className="text-center">
              <h1
                className="font-serif text-[1.75rem] leading-tight sm:text-[2.625rem] sm:leading-[2.8125rem]"
                data-testid="text-auth-heading"
              >
                {title}
              </h1>
              <h2 className="mt-1 text-base leading-6 font-normal">{subtitle}</h2>
            </div>

            {/* PRD 08 D21: an "I already have an account" / merge is waiting on this sign-in. */}
            {stage === "login" && <AccountClaimBanner />}
            {stage !== "forgot" && (
              <>
                <AuthSocial className="gap-3" />
                {/* Legacy `.split-title`: serif label with a hairline rule either side. */}
                <p className="flex w-full max-w-[450px] items-center gap-4 font-serif text-base before:h-px before:flex-1 before:bg-border before:content-[''] after:h-px after:flex-1 after:bg-border after:content-['']">
                  OR use an email address
                </p>
              </>
            )}

            {stage === "login" && (
              <LoginForm
                initialError={oauthError}
                onForgotClick={(typed) => {
                  setForgotEmail(typed);
                  goToStage("forgot");
                }}
                onSwitch={() => goToStage("register")}
              />
            )}
            {stage === "register" && (
              <RegisterForm initialError={oauthError} onSwitch={() => goToStage("login")} />
            )}
            {stage === "forgot" && (
              <ForgotPasswordForm
                defaultEmail={forgotEmail || undefined}
                onBack={() => goToStage("login")}
                notice={
                  new URLSearchParams(window.location.search).get("error") === "legacy_reset_link"
                    ? oauthError
                    : undefined
                }
              />
            )}

            {isDevEnv && <DemoPersonaPicker />}
          </div>
        </div>
      </main>

      {/* Legacy: login/register keep the full footer, /forgot gets the simple one. */}
      {stage === "forgot" ? <SimpleFooter /> : <SiteFooter />}
    </div>
  );
}
